import React, { useEffect, useMemo, useState } from "react";
import PropertySelect from "./PropertySelect";
import RecipientItem from "../CampaignSteps/components/RecipientItem";
import { GroupOption, PropertyState } from "./Step3.types";
import { getGroupsByProperties } from "PhishingManager/service/index.Service";
import DebounceInput from "PhishingManager/components/DebouncedInput";
import SelectedLabel from "./SelectedLabel";


interface Props {
    loading: boolean;
    tenentId: number;
    propertyData: PropertyState;
    selectedGroups: GroupOption[];
    selectedProperties: number[];
    allPropertiesSelected: boolean;
    setSelectedGroups: (groups: GroupOption[]) => void;
    toggleLoading: (status: boolean) => void;
    setSelectedProperties: (propertyIds: number[]) => void;
    setPropertySearchQuery: (val: string | null) => void;
    moveCurrentPage: () => void;
    setAllPropertiesSelected: (val: boolean) => void;
}

const GroupRecipients: React.FC<Props> = ({
    tenentId,
    loading,
    propertyData,
    selectedGroups,
    selectedProperties,
    allPropertiesSelected,
    setSelectedGroups,
    toggleLoading,
    setSelectedProperties,
    setPropertySearchQuery,
    moveCurrentPage,
    setAllPropertiesSelected
}) => {
    const [search, setSearch] = useState("");
    const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
    const [allGroupsSelected, setAllGroupsSelected] = useState(false);

    const filteredGroups = useMemo(() => {
        return groupOptions.filter((group) =>(allPropertiesSelected || selectedProperties.includes(group.propertyId)) ||(search ? group.label.toLowerCase().includes(search.toLowerCase()) : group));
    }, [groupOptions, selectedProperties, allPropertiesSelected, search]);

    const totalUsersCount = useMemo(() => groupOptions.reduce((acc, group) => {
        const isSelected = selectedGroups.find((s) => s.value === group.value);
        return isSelected ? acc + group.count : acc;
    }, 0), [filteredGroups, selectedGroups])

    const handleGroupToggle = (groupItem: GroupOption) => {
        const isSelected = selectedGroups.some((g) => g.value === groupItem.value);
    
        setSelectedGroups(isSelected ? selectedGroups.filter((g) => g.value !== groupItem.value) : [...selectedGroups, groupItem]);
    };

    const getGroups = async () => {
        try {
            if (!tenentId) return;
            toggleLoading(true);
            const res = await getGroupsByProperties(Number(tenentId), selectedProperties, false); //false should be replace with allPropertiesSelected
            const groups = (res?.data || []).map((group: any) => ({
                value: group.venza_group_id,
                label: group.group_name,
                count: group.user_count || 0,
                propertyId: group.property_id || 0
            }))
            setGroupOptions(groups);
        } catch (error) {
            setGroupOptions([]);
        } finally {
            toggleLoading(false);
        }
    };

    useEffect(() => {
        if (selectedProperties.length) getGroups();
        else setGroupOptions([]);
    }, [selectedProperties])

    useEffect((() => {
        if (!!allGroupsSelected) setSelectedGroups([...filteredGroups]);
        else setSelectedGroups([]);
    }), [allGroupsSelected]);

    useEffect((() => {
        if (!allPropertiesSelected) setSelectedGroups([]);
    }), [allPropertiesSelected]);

    return (
        <div className="phishing-step3__role-section">
            <div className="phishing-step3__role-filters">
                <div className="phishing-step3__filter-group">
                    <div className="phishing-step3__label">
                        Select property <span className="required">*</span>
                    </div>
                    <PropertySelect {...{ loading, propertyData, selectedProperties, setSelectedProperties, allSelected: allPropertiesSelected, setAllSelected: setAllPropertiesSelected, moveCurrentPage, setSearchValue: setPropertySearchQuery }}
                        onPropertyDeselect={(propertyValue) => {
                            setSelectedGroups(
                                selectedGroups.filter((group) => {
                                    const selGroup = filteredGroups.find((g) => g.value === group.value);
                                    return selGroup?.propertyId !== propertyValue;
                                })
                            );
                        }}
                    />
                </div>

                <div className="phishing-step3__filter-group phishing-step3__role-search">
                    <div>
                        <DebounceInput placeholder="Search groups" setDebouncedSearch={(value) => setSearch(value)} />
                    </div>
                </div>
            </div>

            <SelectedLabel labelText={`Select a group ${filteredGroups.length > 0 ? `(${filteredGroups.length} Results)` : ''}`} totalCount={totalUsersCount} selectedAll={(filteredGroups.length > 0 && selectedGroups.length === filteredGroups.length)} handleSelectAll={() => setAllGroupsSelected(!allGroupsSelected)} />

            <div className="phishing-step3__role-list">
                {selectedProperties.length === 0 && !allPropertiesSelected ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        Please select a property to see groups.
                    </div>
                ) : filteredGroups.length === 0 ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        No groups found.
                    </div>
                ) : (
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                        {filteredGroups.map((group) => (
                            <RecipientItem
                                key={group.value}
                                title={group.label}
                                description={`${group.count} users`}
                                iconType="recipientGroup"
                                isChecked={selectedGroups.some(g => g.value === group.value)}
                                onToggle={() => handleGroupToggle(group)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GroupRecipients;